import { Play, Repeat, RefreshCw } from 'lucide-react';
import { useState } from 'react';

import { AdminPage } from '@/components/admin/AdminPage';
import { Button } from '@/components/ui/button';
import { DataTable, type Column } from '@/components/ui/data-table';
import { EmptyState } from '@/components/ui/empty-state';
import { FilterPills } from '@/components/ui/filter-pills';
import { Pagination } from '@/components/ui/pagination';
import { StatusBadge } from '@/components/ui/status-badge';
import {
  useGenerateRecurringJobs,
  useRecurringRules,
  useSetRecurringRuleActive,
} from '@/hooks/recurring-jobs';
import { ApiError } from '@/lib/api';
import { formatDay, formatDuration, formatWallTime } from '@/lib/dates';
import { useToast } from '@/lib/toast-context';
import { RecurringRuleDialog } from '@/pages/admin/recurring/RecurringRuleDialog';
import { ACTIVITY_FILTER_OPTIONS, type ActivityFilter } from '@shared/schemas/pagination';
import type { RecurringRuleView } from '@shared/schemas/recurring';

/*
 * /admin/recurring-jobs — ROADMAP.md §7.5.
 *
 * A rule is a description of work that repeats; the jobs it produces are
 * ordinary jobs and live on the jobs screen. That separation is what makes
 * pausing a rule safe — business-rules.md §6: deactivating stops future
 * generation and leaves already-generated jobs alone, because they have crews
 * and possibly photos against them.
 *
 * **"Generate now" is safe to press twice**, and the toast says so by reporting
 * the skips. That is the visible face of the idempotency the whole slice is
 * built on: a second press showing "0 created, 14 skipped" is the feature
 * working, and an office that learns that is an office that will happily press
 * it when they are not sure whether last night's run happened.
 */

export function RecurringJobsPage() {
  const toast = useToast();
  const [page, setPage] = useState(1);
  const [activity, setActivity] = useState<ActivityFilter>('active');
  const [editing, setEditing] = useState<RecurringRuleView | null>(null);
  const [creating, setCreating] = useState(false);

  const rules = useRecurringRules({ page, activity });
  const generate = useGenerateRecurringJobs();

  const columns: Column<RecurringRuleView>[] = [
    {
      key: 'client',
      header: 'Client',
      render: (rule) => (
        <div className="min-w-0">
          <p className="truncate text-body font-medium text-ink-900">{rule.clientName}</p>
          <p className="truncate text-meta text-ink-500">
            {rule.propertyAddress ?? 'Their primary property'}
          </p>
        </div>
      ),
    },
    {
      key: 'schedule',
      header: 'Repeats',
      render: (rule) => (
        <div className="min-w-0">
          {/* A phrase, never `biweekly` and never a day number (P5). */}
          <p className="text-body text-ink-900">
            {rule.frequencyLabel} on {rule.scheduleLabel}
          </p>
          <p className="text-meta text-ink-500">
            {formatWallTime(rule.preferredTime)} · {formatDuration(rule.estimatedDurationMinutes)}
          </p>
        </div>
      ),
    },
    {
      key: 'service',
      header: 'Service',
      render: (rule) => <span className="text-body text-ink-700">{rule.serviceTypeName}</span>,
    },
    {
      key: 'crew',
      header: 'Usual crew',
      render: (rule) =>
        rule.defaultEmployeeNames.length === 0 ? (
          <span className="text-meta text-ink-500">Nobody — jobs arrive pending</span>
        ) : (
          <span className="text-meta text-ink-700">{rule.defaultEmployeeNames.join(', ')}</span>
        ),
    },
    {
      key: 'next',
      header: 'Next',
      render: (rule) => (
        <div className="min-w-0">
          <p className="text-body text-ink-900">
            {rule.isActive ? formatDay(rule.nextGenerateDate) : 'Paused'}
          </p>
          {/* Zero is `0` (P1) — a rule that has produced nothing is a fact. */}
          <p className="text-meta text-ink-500">
            {rule.generatedJobCount} job{rule.generatedJobCount === 1 ? '' : 's'} so far
          </p>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (rule) => (
        <StatusBadge
          tone={rule.isActive ? 'scheduled' : 'cancelled'}
          label={rule.isActive ? 'Active' : 'Paused'}
        />
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      hideLabelOnCard: true,
      render: (rule) => <RowActions rule={rule} onEdit={setEditing} />,
    },
  ];

  return (
    <AdminPage
      title="Recurring Jobs"
      description="Standing arrangements — weekly, fortnightly and monthly cleans that appear on the calendar by themselves."
      actions={
        <>
          <Button
            variant="outline"
            disabled={generate.isPending}
            onClick={() => {
              generate.mutate(undefined, {
                onSuccess: (result) => {
                  /*
                   * The skip count is deliberately in the message. "0 created,
                   * 14 skipped" is the idempotency working, and an office that
                   * has read it once will press this again without worrying.
                   */
                  toast.success(
                    `${String(result.created)} job${result.created === 1 ? '' : 's'} created, ${String(result.skipped)} already there.` +
                      (result.failures.length > 0
                        ? ` ${String(result.failures.length)} rule${result.failures.length === 1 ? '' : 's'} could not run — check them below.`
                        : ''),
                  );
                },
                onError: (error) => {
                  toast.error(
                    error instanceof ApiError ? error.message : 'Generation could not be run.',
                  );
                },
              });
            }}
          >
            <RefreshCw aria-hidden="true" />
            {generate.isPending ? 'Generating…' : 'Generate now'}
          </Button>
          <Button
            onClick={() => {
              setCreating(true);
            }}
          >
            <Repeat aria-hidden="true" />
            Add a recurring job
          </Button>
        </>
      }
    >
      <div className="mb-4">
        <FilterPills
          label="Filter recurring jobs"
          options={ACTIVITY_FILTER_OPTIONS}
          value={activity}
          onChange={(next) => {
            setActivity(next);
            setPage(1);
          }}
        />
      </div>

      <DataTable
        caption="Recurring jobs, with what they repeat and when the next one is due"
        columns={columns}
        rows={rules.data?.data ?? []}
        rowKey={(rule) => rule.id}
        isLoading={rules.isPending}
        error={rules.error}
        onRetry={() => {
          void rules.refetch();
        }}
        empty={
          <EmptyState
            icon={Repeat}
            title="No recurring jobs yet."
            hint="A standing arrangement — every Tuesday, the first of the month — puts jobs on the calendar without anybody typing them."
            action={
              <Button
                onClick={() => {
                  setCreating(true);
                }}
              >
                Add a recurring job
              </Button>
            }
          />
        }
      />

      <Pagination
        page={page}
        pageSize={rules.data?.pageSize ?? 25}
        total={rules.data?.total ?? 0}
        onPageChange={setPage}
        itemNoun="recurring jobs"
      />

      <RecurringRuleDialog
        key={creating ? 'new' : (editing?.id ?? 'closed')}
        open={creating || editing !== null}
        rule={editing ?? undefined}
        onOpenChange={(next) => {
          if (!next) {
            setCreating(false);
            setEditing(null);
          }
        }}
      />
    </AdminPage>
  );
}

/** Pause, resume, edit. No delete — a rule is the provenance of its jobs. */
function RowActions({
  rule,
  onEdit,
}: {
  rule: RecurringRuleView;
  onEdit: (rule: RecurringRuleView) => void;
}) {
  const toast = useToast();
  const setActive = useSetRecurringRuleActive(rule.id);

  return (
    <div className="flex flex-wrap justify-end gap-2">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => {
          onEdit(rule);
        }}
      >
        Edit
      </Button>
      <Button
        variant="outline"
        size="sm"
        disabled={setActive.isPending}
        onClick={() => {
          setActive.mutate(!rule.isActive, {
            onSuccess: (saved) => {
              toast.success(
                saved.isActive
                  ? `${saved.clientName}'s ${saved.frequencyLabel.toLowerCase()} clean is running again.`
                  : // Says what it does *not* do, because that is the question
                    // somebody pausing one is actually asking.
                    `Paused. Jobs already on the calendar are untouched.`,
              );
            },
            onError: (error) => {
              toast.error(error instanceof ApiError ? error.message : 'That could not be saved.');
            },
          });
        }}
      >
        {rule.isActive ? (
          'Pause'
        ) : (
          <>
            <Play aria-hidden="true" />
            Resume
          </>
        )}
      </Button>
    </div>
  );
}
